import { DICTIONARY } from 'Enum';
import { Dictionary } from 'Dictionary';
import { CustomMetaTitle, LinkReturnToMain, NoDataFound2 } from 'Components';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { Avatar, Button, Stack, Typography } from '@mui/material';
import { PurpleArrowDown2 } from 'Images';
import { Title } from './Title';
import { UpdatesList } from './UpdatesList';
import { FilterTags } from './FilterTags';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IUserUpdates, getUpdates } from 'API/Home/userUpdates';
import { SortAndCountRow } from './UpdatesList/SortAndCountRow';
import { useLoader } from 'Hooks/useLoader';
import { useCustomDialog } from 'Hooks/useCustomDialog';

const MAX_ITEMS_TO_DISPLAY = 10;

export const Updates = () => {
  const isDesktop = useIsDesktop();
  const { dialog, setShowDialog } = useCustomDialog();

  const dictionary = useDictionary(Dictionary, DICTIONARY.Updates);
  const [displayedUpdateCount, setDisplayedUpdateCount] =
    useState(MAX_ITEMS_TO_DISPLAY);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [updates, setUpdates] = useState<IUserUpdates[]>([]);
  const [isSortDesc, setIsSortDesc] = useState(true);
  const { isLoading, setIsLoading, loader } = useLoader();

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const FILTER_LABEL: { [key: number]: string } = {
    1: dictionary.filterLabels.medicals,
    2: dictionary.filterLabels.docs,
    3: dictionary.filterLabels.payments,
    4: dictionary.filterLabels.applies,
  };

  const initializeData = useCallback(async () => {
    setIsLoading(true);
    const updates = await getUpdates();
    if (updates && updates.data) {
      const dataWithLabels = updates.data.map((update) => ({
        ...update,
        filterLabel: FILTER_LABEL[update.notifEntityId],
      }));

      setUpdates(dataWithLabels);
    }
    setIsLoading(false);
  }, [setIsLoading]);

  useEffect(() => {
    initializeData();
  }, [initializeData, imposterId]);

  const redirects = [
    { text: dictionary.redirect1, href: '/universalRequest' },
    { text: dictionary.redirect2, href: '/compass-rights' },
    { text: dictionary.redirect3, href: '/personal-team?Contact=1' },
  ];

  const handleSort = useCallback(() => {
    setIsSortDesc((prevIsSortDesc) => !prevIsSortDesc);
  }, []);

  const filterBySubjects = (
    updates: IUserUpdates[],
    selectedSubjects: string[]
  ): IUserUpdates[] => {
    if (selectedSubjects.length === 0) return updates;

    const filteredBySubjects: IUserUpdates[] = [];
    for (const update of updates) {
      if (update.filterLabel && selectedSubjects.includes(update.filterLabel)) {
        filteredBySubjects.push(update);
      }
    }
    return filteredBySubjects;
  };

  const sortUpdates = useCallback(
    (updates: IUserUpdates[], isSortDesc: boolean): IUserUpdates[] => {
      if (isSortDesc) {
        return [...updates].sort(
          (a: IUserUpdates, b: IUserUpdates) =>
            new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
        );
      } else {
        return [...updates].sort(
          (a: IUserUpdates, b: IUserUpdates) =>
            new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
        );
      }
    },
    []
  );

  const subjects = useMemo(() => {
    const subjectNames = updates
      .map((update) => update.filterLabel)
      .filter(Boolean);
    return [...new Set(subjectNames)];
  }, [updates]);

  const filteredUpdates = useMemo(() => {
    let filteredAndSortedUpdates = [...updates];

    if (selectedSubjects.length > 0) {
      filteredAndSortedUpdates = filterBySubjects(
        filteredAndSortedUpdates,
        selectedSubjects
      );
    }

    filteredAndSortedUpdates = sortUpdates(
      filteredAndSortedUpdates,
      isSortDesc
    );

    return filteredAndSortedUpdates?.slice(0, displayedUpdateCount);
  }, [
    updates,
    selectedSubjects,
    isSortDesc,
    displayedUpdateCount,
    sortUpdates,
  ]);

  const handleShowMore = () => {
    setDisplayedUpdateCount((prevCount) => prevCount + MAX_ITEMS_TO_DISPLAY);
  };

  return (
    <>
      <CustomMetaTitle title={dictionary.title} />
      <LinkReturnToMain link='/' />
      {isLoading ? (
        loader
      ) : (
        <>
          {updates.length > 0 ? (
            <Stack
              gap={isDesktop ? '28px' : '32px'}
              maxWidth={isDesktop ? '960px' : 'initial'}
            >
              <Title />
              <FilterTags
                reset={() => setSelectedSubjects([])}
                clearSelectedSubjects={() => setSelectedSubjects([])}
                selectedSubjects={selectedSubjects}
                setSelectedSubjects={setSelectedSubjects}
                subjects={subjects}
              />
              <SortAndCountRow
                count={filteredUpdates.length}
                sort={handleSort}
                sortingOrder={isSortDesc}
              />
              <Stack>
                <UpdatesList
                  data={filteredUpdates.slice(0, displayedUpdateCount)}
                  setShowDialog={setShowDialog}
                />
              </Stack>
              {displayedUpdateCount <= filteredUpdates.length && (
                <Button
                  onClick={handleShowMore}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    m: '28px auto',
                    height: isDesktop ? '50px' : '30px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <Typography variant='body5'>
                    {dictionary.previousUpdates}
                  </Typography>
                  <Avatar
                    alt=''
                    src={PurpleArrowDown2}
                    sx={{
                      height: '44px',
                      width: '50px',
                      ml: 1,
                    }}
                  />
                </Button>
              )}
              {dialog}
            </Stack>
          ) : (
            <NoDataFound2
              gap={isDesktop ? '48px' : '32px'}
              redirects={redirects}
              headerComponent={
                <Stack alignItems={'center'} gap={'4px'}>
                  <Typography
                    variant='h2'
                    color='text.secondary'
                    component='h1'
                  >
                    {dictionary.title}
                  </Typography>
                  <Typography
                    variant={'body3'}
                    color='text.secondary'
                    mt={'12px'}
                  >
                    {dictionary.noUpdatesSubtitle}
                  </Typography>
                </Stack>
              }
            />
          )}
        </>
      )}
    </>
  );
};
